import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createSelector, EntityState } from '@reduxjs/toolkit';
import { selectLanguage } from '@ff-os/pdp-shared-redux/slices/globalSlice';
import { selectIsLoadingProject } from '@ff-os/pdp-shared-redux/slices/projectLoadingSlice';
import { createEngine2d, Engine2d, Engine2dOptions } from '@ff-os/rendering-react/createEngine2d';
import { Clip } from '@ff-os/rendering-shared/Clip';
import { Spaces } from '@ff-os/rendering-shared/coordinates';
import { Spinner } from '@ff-os/shared-react/components/loaders/spinner/Spinner';
import { usePrevious } from '@ff-os/shared-react/hooks/usePrevious';
import { useSize } from '@ff-os/shared-react/hooks/useSize';
import { unwrap } from '@ff-os/shared-utils/unwrap';
import { selectNonPinnedVariables, selectVariablePaintables } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { GiftJson } from '@ff-scene-plugins/gift-shared/GiftJson';
import { Config3d } from '@ff-scene-plugins/gift-shared/GiftVariantOptions';
import useIntersectionObserver from '@react-hook/intersection-observer';
import classNames from 'classnames';
import { Paintable, Size } from 'ff-canvas';
import { clamp, isEqual } from 'lodash';
import { selectConfig3d, selectSpacesForFullPreview2d, selectVariantPreviewClip } from '../_redux-variants';
import { selectInputsWithoutComputedProperties } from '../helpers/selectInputsWithoutComputedProperties';
import { Engine3d, loadEngine3d } from './helpers/loadEngine3d';
import styles from '../GiftPreviewUi.module.scss';

const _GiftPreview3d = ({
    paintables, config3d,
    spaces, previewClip,
    language, isLoadingProject,
    variables, inputs
}: Props) => {

    const containerRef = useRef<HTMLDivElement>(null);
    const containerSize = useSize(containerRef);

    const canvas2dRef = useRef<HTMLCanvasElement>(null);
    const paintablesRef = useRef<Paintable[]>([]);
    const [engine2d, setEngine2d] = useState<Engine2d | null>(null);

    const canvas3dRef = useRef<HTMLCanvasElement>(null);
    const activeConfig3dRef = useRef<Config3d | null>(null);
    const [engine3d, setEngine3d] = useState<Engine3d | undefined>();

    const { isIntersecting: shouldRender } = useIntersectionObserver(containerRef);

    const { t } = useTranslation();

    // 3D engine creation:

    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {
            if (!engine3d || config3d !== activeConfig3dRef.current) {
                if (shouldRender) {
                    activeConfig3dRef.current = config3d;
                    ((async () => {
                        const newEngine3d = await loadEngine3d(canvas3dRef.current!, config3d!);
                        if (activeConfig3dRef.current === config3d) {
                            engine3d?.dispose();
                            setEngine3d(newEngine3d);
                            setEngine2d(null);
                        }
                    })());
                }
            }
        }
    }, [shouldRender, engine3d, config3d]);

    // 3D engine resize:

    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {
            if (containerSize && engine3d) {
                resize(canvas3dRef.current!, containerSize);
                engine3d.scene3d.updateDimensions(containerSize);
            }
        }
    }, [engine3d, containerSize]);

    // 3D engine disposal on unmount/change:

    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {
            if (engine3d) {
                return () => {
                    engine3d?.dispose();
                };
            }
        }
    }, [engine3d]);

    // 2D backing engine creation:

    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {

            const renderSize2d = calculateRenderSize2d(spaces);

            if (engine3d && !engine2d?.is(renderSize2d, spaces)) {

                engine2d?.dispose();

                const options: Engine2dOptions = {
                    onRender: engine3d.scene3d.changeTextureAsync,
                    previewClip: previewClip,
                    useDevicePixelRatio: false
                };
                setEngine2d(createEngine2d(
                    paintablesRef, canvas2dRef.current!, renderSize2d, spaces,
                    { t: t, language }, options));
            }
        }
    }, [engine3d, engine2d, spaces, t, language, previewClip]);

    // 2D backing engine paintables rerender:

    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {
            if (engine2d) {
                paintablesRef.current = paintables;
                engine2d.requestRender();
            }
        }
    }, [engine2d, paintables]);

    const previousInputs = usePrevious(inputs);
    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {
            if (engine3d && variables.length > 0) {
                const angle = findRotationAngle(unwrap(previousInputs), unwrap(inputs), variables);
                if (angle !== null && angle >= 0) {
                    engine3d.scene3d.rotateCameraTo(angle);
                }
            }
        }
    }, [engine3d, variables, inputs, previousInputs]);

    const isConfig3dActive = config3d === activeConfig3dRef.current;
    const showSpinner = isLoadingProject || !engine3d || !isConfig3dActive;

    const canvasClassName = classNames('idt-preview-3d', {
        [styles.hidden]: !isConfig3dActive,
        [styles.canvas]: isConfig3dActive
    });

    return (
        <>
            <div ref={containerRef} className={styles.container}>
                {showSpinner && <Spinner overlaySize='full' spinnerSize='medium' />}
                <canvas
                    ref={canvas3dRef}
                    className={canvasClassName}
                    style={{ 'backgroundColor': config3d!.backgroundColor }} />
            </div>
            <canvas ref={canvas2dRef} style={{ display: 'none' }} />
        </>
    );
};

type Props = PropsWithChildren<{
    paintables: Paintable[];
    config3d: Config3d;
    spaces: Spaces;
    previewClip: Clip | undefined;
    language: string;
    isLoadingProject: boolean;
    variables: GiftJson.Variable<any>[];
    inputs: EntityState<GiftJson.Input<any>>;
}>;

const mapStateToProps = (state: GiftPdpState) => ({
    paintables: selectVariablePaintables(state, 'preview3d'),
    config3d: selectConfig3d(state)!,
    spaces: selectSpacesForFullPreview2d(state),
    previewClip: selectVariantPreviewClip(state),
    isLoadingProject: selectIsLoadingProject(state),
    language: selectLanguage(state),
    variables: selectVariablesWithRotateOption(state),
    inputs: selectInputsWithoutComputedProperties(state)
});

export const GiftPreview3d = connect(
    mapStateToProps
)(_GiftPreview3d);

const findRotationAngle = (
    oldInputs: GiftJson.Input<any>[],
    newInputs: GiftJson.Input<any>[],
    variables: GiftJson.Variable<any>[]
): number | null => {

    for (const newInput of newInputs) {
        const oldInput = oldInputs.find(oldInput => oldInput.id === newInput.id);
        if (oldInput && newInput && !isEqual(oldInput, newInput)) {
            return variables.find(v => v.id === newInput.id)?.options?.rotateCameraToAngle!;
        }
    }

    return null;
};

const selectVariablesWithRotateOption = createSelector([
    selectNonPinnedVariables
], variables => variables.filter(v => v.options.rotateCameraToAngle >= 0));

const resize = (
    canvas3d: HTMLCanvasElement,
    containerSize: Size
) => {
    canvas3d.width = containerSize.width;
    canvas3d.height = containerSize.height;
    canvas3d.style.width = containerSize.width + 'px';
    canvas3d.style.height = containerSize.height + 'px';
};

const calculateRenderSize2d = (spaces: Spaces) => {

    const minTextureSize = 500;
    const maxTextureSize = 800;

    const screenSize = Math.max(window.screen.width, window.screen.height);
    const textureSize = clamp(screenSize, minTextureSize, maxTextureSize);

    const aspectRatio = spaces.sceneWidth / spaces.sceneHeight;

    return {
        width: aspectRatio > 1 ? textureSize : textureSize * aspectRatio,
        height: aspectRatio < 1 ? textureSize : textureSize / aspectRatio
    };
};